import { Button } from '@creditornot/cb-button';
import { DatePicker } from '@creditornot/cb-date-picker';
import { Calendar } from '@creditornot/cb-icons';
import { Input } from '@creditornot/cb-input';
import { LegacyPopover } from '@creditornot/cb-popover';
import { DateTime } from 'luxon';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import TimePicker from 'common/components/weekly-schedules/TimePairInput/TimePicker';

import { MenuLocaleContext } from '../../../MenuLocaleContext';

type Props = {
  disabled: boolean;
  onChange: (value: number | null) => void;
  value: number | null;
};

const Root = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
`;

const PickerContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px;
`;

const Section = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-block-start: 16px;
`;

const StyledTimePicker = styled(TimePicker)`
  width: 64px;
`;

const CancelButton = styled(Button).attrs({ size: 'small', variant: 'lightBlue' })`
  margin-inline-end: 12px;
`;

const DoneButton = styled(Button).attrs({ size: 'small', variant: 'blue' })``;

const checkForDST = (start: DateTime, current: DateTime) => {
  if (!start.isInDST && current.isInDST) {
    return current.minus({ hours: 1 });
  }

  if (start.isInDST && !current.isInDST) {
    return current.plus({ hours: 1 });
  }

  return current;
};

const AvailabilityDateTimePicker = ({ disabled, onChange, value }: Props) => {
  const { timezone } = useContext(MenuLocaleContext);
  const intl = useIntl();

  const initialValues = useMemo(() => {
    const valueAsDateTime = value ? DateTime.fromMillis(value).setZone(timezone) : null;

    return {
      dateValue: valueAsDateTime
        ? new Date(valueAsDateTime.year, valueAsDateTime.month - 1, valueAsDateTime.day)
        : undefined,
      timeValue: valueAsDateTime ? valueAsDateTime.hour * 60 + valueAsDateTime.minute : 0,
      inputValue: valueAsDateTime ? valueAsDateTime.toLocaleString(DateTime.DATETIME_SHORT) : '',
    };
  }, [timezone, value]);

  useEffect(() => {
    setDateValue(initialValues.dateValue);
    setTimeValue(initialValues.timeValue);
  }, [initialValues]);

  const [open, setOpen] = useState(false);
  const [dateValue, setDateValue] = useState<Date | undefined>(initialValues.dateValue);
  const [timeValue, setTimeValue] = useState<number>(initialValues.timeValue);

  const resetToInitialValues = () => {
    setDateValue(initialValues.dateValue);
    setTimeValue(initialValues.timeValue);
  };

  const handleClear = () => {
    onChange(null);
  };

  const handleDone = () => {
    // Should always be defined since we disable the "Done" button if undefined
    // But check to satisfy TS
    if (dateValue) {
      const date = DateTime.fromObject(
        {
          year: dateValue.getFullYear(),
          month: dateValue.getMonth() + 1,
          day: dateValue.getDate(),
        },
        {
          zone: timezone,
        },
      ).startOf('day');

      const combinedDate = date.plus({ minutes: timeValue });
      const dstAdjustedDate = checkForDST(date, combinedDate);

      onChange(dstAdjustedDate.toMillis());
    }
    setOpen(false);
  };

  const handleCancel = () => {
    resetToInitialValues();
    setOpen(false);
  };

  return (
    <Root>
      <LegacyPopover
        contentZIndex={7}
        onClickOutside={handleCancel}
        placement={'bottom-start'}
        portalElement={null}
        renderArrow={false}
        show={open}
        content={
          <PickerContainer>
            <DatePicker firstDayOfWeek={1} value={dateValue} onChange={val => setDateValue(val)} />
            <Section>
              <StyledTimePicker value={timeValue} onChange={val => setTimeValue(val)} />
              <div>
                <CancelButton onClick={handleCancel}>
                  <FormattedMessage id="state.cancel" />
                </CancelButton>
                <DoneButton onClick={handleDone} disabled={dateValue === undefined}>
                  <FormattedMessage id="state.done" />
                </DoneButton>
              </div>
            </Section>
          </PickerContainer>
        }
      >
        <Input
          disabled={disabled}
          icon={<Calendar />}
          onClearClick={handleClear}
          onFocus={() => {
            setOpen(true);
          }}
          placeholder={intl.formatMessage({ id: 'date-time.date-time-input-placeholder' })}
          readOnly
          value={initialValues.inputValue}
        />
      </LegacyPopover>
    </Root>
  );
};

export default AvailabilityDateTimePicker;
